/**
 * Policy View Dialog Component
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { THEME_COLOR } from '../../constants/policies';
import { formatHotelDate, formatHotelDateTime } from '../../utils/dateUtils';

export const PolicyViewDialog = ({
  open,
  policy,
  isDark,
  onClose,
}) => {
  if (!policy) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatHotelDate(dateString) || dateString;
  };

  const getPolicyTypeLabel = (type) => {
    const typeMap = {
      terms_and_conditions: 'Terms and Conditions',
      privacy_policy: 'Privacy Policy',
      child_policy: 'Child Policy',
      cancellation_policy: 'Cancellation Policy',
      refund_policy: 'Refund Policy',
      check_in_policy: 'Check-in Policy',
      check_out_policy: 'Check-out Policy',
      pet_policy: 'Pet Policy',
      smoking_policy: 'Smoking Policy',
      age_restriction_policy: 'Age Restriction Policy',
    };
    return typeMap[type] || type;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {policy.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={getPolicyTypeLabel(policy.policy_type)}
                size="small"
                sx={{
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                  color: THEME_COLOR,
                  fontWeight: 500,
                }}
              />
              <Chip
                label={`Version ${policy.version}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={policy.is_active ? 'Active' : 'Inactive'}
                size="small"
                color={policy.is_active ? 'success' : 'default'}
              />
              {policy.requires_acknowledgment && (
                <Chip
                  label="Requires Acknowledgment"
                  size="small"
                  color="warning"
                />
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: 'text.secondary', ml: 2 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Summary */}
          {policy.summary && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                Summary
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                {policy.summary}
              </Typography>
            </Box>
          )}

          {/* Content */}
          {policy.content && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                Content
              </Typography>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: isDark ? '#2a2a2a' : '#f5f5f5',
                  color: 'text.primary',
                  '& p': { margin: '0.5em 0' },
                  '& ul, & ol': { margin: '0.5em 0', paddingLeft: '2em' },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& blockquote': {
                    borderLeft: 4,
                    borderColor: THEME_COLOR,
                    paddingLeft: 2,
                    margin: '1em 0',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: policy.content }}
              />
            </Box>
          )}

          <Divider />

          {/* Metadata */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              Policy Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Effective Date:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {formatDate(policy.effective_date)}
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Expiry Date:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {formatDate(policy.expiry_date)}
              </Typography>

              {policy.created_at && (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Created:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {formatHotelDateTime(policy.created_at)}
                  </Typography>
                </>
              )}

              {policy.updated_at && (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Last Updated:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {formatHotelDateTime(policy.updated_at)}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ backgroundColor: THEME_COLOR, '&:hover': { backgroundColor: '#3ab89a' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

